/**
 * `<WhatNeedsYouSection/>` — member-side actionable summary for the persistent right panel (PR 4, #200).
 *
 * Closes T3's click-depth complaint per the IA-rework doc §6 PR 4: a
 * member previously had to navigate via workspace switcher → team
 * picker → cycles list to reach their submission flow. This section
 * surfaces those actionable items in the panel that's visible on
 * every `/me/*` page, each with a one-click deep link.
 *
 * Always-on per #193 (core member affordance — the worker-trust pillar
 * doesn't gate "what you need to do today" behind a registry component).
 *
 * **v0.1 SPA-only synthesis** per the engineering-lead recommendation
 * in IA-rework doc §6 PR 4: composed from existing endpoints, no new
 * `/me/workload-summary` route. Promotes to a server endpoint when the
 * cohort grows past N=10 members.
 *
 * **Data sources.**
 *   - `useWorkspaces()` — already cached. `cycle_count_open` per team
 *     drives the "open cycles waiting for input" rows.
 *   - `useMySchedule()` — `/api/account/me/schedule` returns the
 *     caller's published assignments over the next horizon_days. We
 *     filter to the next 7 days for the "upcoming shifts" tally.
 *
 * **Rendered shape.**
 *   - One row per workspace with `cycle_count_open > 0`: "Team X: N
 *     open cycle(s)" → deep link to `/teams/{teamId}` (the cycles
 *     list — works for members; the lead-only members tab silently
 *     hides for non-leads but the cycles tab is the page's default).
 *   - One aggregate row when there are upcoming shifts: "X shifts in
 *     the next 7 days" → deep link to `/me`.
 *   - "Nothing today." calm-success state when neither row applies.
 *
 * **Self-hides** on fetch error or before either query resolves
 * (shows a single skeleton row in the meantime).
 *
 * **Focal-component discipline** per `feedback_ui_section_focal_component.md`:
 * the actionable rows are the focal element; the section header +
 * deep-links are calm supporting context. The "all caught up" success
 * state uses a green check as its single focal anchor.
 */
import { AlertCircle, CheckCircle2, ClipboardList, Clock } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { useMySchedule } from "../api/useMySchedule";
import { useWorkspaces } from "../api/useWorkspaces";
import { Skeleton } from "./ui";

const UPCOMING_WINDOW_DAYS = 7;

interface OpenCycleRow {
  teamId: string;
  teamName: string;
  count: number;
}

/**
 * The section's reads + derived rows, shared with the member panel
 * handle's badge (#1920, `lib/persistentPanelBadge.ts`). `itemCount` is the
 * number of rows the section lists (one per team with open schedules, plus
 * one for upcoming shifts); 0 while loading or on a workspaces error, when
 * the section shows no rows either. `enabled: false` holds the schedule
 * read (the workspaces read is the AppShell's own, already cached).
 */
export function useWhatNeedsYou(opts: { enabled?: boolean } = {}) {
  const workspacesQuery = useWorkspaces();
  const scheduleQuery = useMySchedule({ enabled: opts.enabled ?? true });

  const openCycleRows = useMemo<OpenCycleRow[]>(() => {
    const rows = workspacesQuery.data ?? [];
    return rows
      .filter((w) => w.cycle_count_open > 0 && w.membership_status === "active")
      .map((w) => ({
        teamId: w.team_id,
        teamName: w.team_name,
        count: w.cycle_count_open,
      }));
  }, [workspacesQuery.data]);

  const upcomingShiftCount = useMemo(() => {
    const assignments = scheduleQuery.data?.assignments ?? [];
    const now = Date.now();
    const cutoff = now + UPCOMING_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return assignments.filter((a) => {
      if (!a.is_published) return false;
      const ts = new Date(a.date).getTime();
      return !Number.isNaN(ts) && ts >= now && ts <= cutoff;
    }).length;
  }, [scheduleQuery.data]);

  const loading = workspacesQuery.isLoading || scheduleQuery.isLoading;
  const itemCount =
    workspacesQuery.isError || loading
      ? 0
      : openCycleRows.length + (upcomingShiftCount > 0 ? 1 : 0);

  return { workspacesQuery, scheduleQuery, openCycleRows, upcomingShiftCount, loading, itemCount };
}

export function WhatNeedsYouSection() {
  const { workspacesQuery, openCycleRows, upcomingShiftCount, loading } = useWhatNeedsYou();

  if (workspacesQuery.isError) return null;
  // The schedule endpoint may 404 for a fresh member with no published
  // assignments yet; we treat that as "0 upcoming shifts" rather than
  // as an error. Only hide on workspaces-side failures.

  const isCaughtUp = !loading && openCycleRows.length === 0 && upcomingShiftCount === 0;

  return (
    <section
      data-testid="what-needs-you-section"
      className="flex flex-col gap-2 px-1 py-2"
      aria-label="What needs you"
    >
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-fg">
          <AlertCircle className="h-3.5 w-3.5 text-fg-muted" aria-hidden="true" />
          What needs you
        </div>
      </header>

      {loading ? (
        <Skeleton className="h-8" data-testid="what-needs-you-loading" />
      ) : isCaughtUp ? (
        <div className="flex items-center gap-2" data-testid="what-needs-you-caught-up">
          <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
          <span className="text-sm font-medium text-fg">Nothing today.</span>
        </div>
      ) : (
        <ul className="flex flex-col gap-1.5" data-testid="what-needs-you-list">
          {openCycleRows.map((row) => (
            <li
              key={row.teamId}
              data-testid={`what-needs-you-open-cycles-${row.teamId}`}
              data-team-id={row.teamId}
              data-cycle-count={row.count}
            >
              <Link
                to={`/teams/${row.teamId}`}
                className="flex items-start gap-2 rounded-sm bg-bg-elevated/60 p-2 hover:bg-bg-elevated"
                data-testid={`what-needs-you-open-cycles-link-${row.teamId}`}
              >
                <ClipboardList
                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-fg"
                  aria-hidden="true"
                />
                <div className="flex flex-col">
                  <span className="text-xs font-medium leading-tight text-fg">{row.teamName}</span>
                  <span className="text-[11px] text-fg-muted">
                    {row.count} open {row.count === 1 ? "schedule" : "schedules"} need{" "}
                    {row.count === 1 ? "your input" : "your input"}
                  </span>
                </div>
              </Link>
            </li>
          ))}
          {upcomingShiftCount > 0 ? (
            <li data-testid="what-needs-you-upcoming-shifts" data-shift-count={upcomingShiftCount}>
              <Link
                to="/me"
                className="flex items-start gap-2 rounded-sm bg-bg-elevated/60 p-2 hover:bg-bg-elevated"
                data-testid="what-needs-you-upcoming-shifts-link"
              >
                <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-fg" aria-hidden="true" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium leading-tight text-fg">
                    {upcomingShiftCount} {upcomingShiftCount === 1 ? "shift" : "shifts"} coming up
                  </span>
                  <span className="text-[11px] text-fg-muted">
                    Within the next {UPCOMING_WINDOW_DAYS} days
                  </span>
                </div>
              </Link>
            </li>
          ) : null}
        </ul>
      )}
    </section>
  );
}
